"use client";

import { useState } from "react";
import Link from "next/link";

export interface FAQItem {
  q: string;
  a: string;
}

interface PDPFAQProps {
  items: FAQItem[];
  contactHref?: string;
  contactLabel?: string;
}

export default function PDPFAQ({
  items,
  contactHref = "#",
  contactLabel = "ASK FROM THE LAB →",
}: PDPFAQProps) {
  const [open, setOpen] = useState<number>(0);

  return (
    <section
      style={{
        padding: "96px var(--bp-edge)",
        borderBottom: "1px solid var(--bp-rule)",
      }}
    >
      <div className="bp-faq-grid">
        <div>
          <div className="bp-mono"><span className="bp-bracket">QUERIES</span></div>
          <h2
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.02em",
              fontSize: "clamp(36px, 4.6vw, 64px)",
              lineHeight: 1.02,
              margin: "20px 0 24px",
              textWrap: "balance" as never,
            }}
          >
            Frequently
            <br />
            <em style={{ fontStyle: "italic", color: "var(--bp-ink-2)" }}>asked.</em>
          </h2>
          <p style={{ color: "var(--bp-ink-2)", maxWidth: 360, margin: 0 }}>
            If you don&rsquo;t find your answer here, our clinical team in Freiburg responds within
            four hours during CET business days.
          </p>
          <Link href={contactHref} className="bp-btn" style={{ marginTop: 24, display: "inline-flex" }}>
            {contactLabel}
          </Link>
        </div>

        <div style={{ borderTop: "1px solid var(--bp-rule)" }}>
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} style={{ borderBottom: "1px solid var(--bp-rule)" }}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "22px 0",
                    display: "grid",
                    gridTemplateColumns: "48px 1fr auto",
                    gap: 16,
                    alignItems: "center",
                    background: "none",
                    border: 0,
                    cursor: "pointer",
                  }}
                >
                  <span className="bp-mono-sm">{String(i + 1).padStart(2, "0")}</span>
                  <span
                    style={{
                      fontFamily: "var(--bp-serif)",
                      fontSize: 20,
                      fontWeight: 300,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.25,
                    }}
                  >
                    {it.q}
                  </span>
                  <span style={{ fontFamily: "var(--bp-mono)", fontSize: 16 }}>
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                {isOpen && (
                  <div
                    style={{
                      paddingLeft: 64,
                      paddingBottom: 24,
                      paddingRight: 48,
                      fontSize: 15,
                      color: "var(--bp-ink-2)",
                      lineHeight: 1.6,
                    }}
                  >
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .bp-faq-grid {
          display: grid;
          grid-template-columns: 1fr 1.6fr;
          gap: 64px;
        }
        @media (max-width: 900px) {
          .bp-faq-grid { grid-template-columns: 1fr; gap: 32px; }
        }
      `}</style>
    </section>
  );
}
